import classNames from "classnames"
import { useRef } from "react"
import type { SwiperRef } from "swiper/react"
import { Swiper, SwiperSlide } from "swiper/react"

import { Subtitle, Title2 } from "@bounce/design-system"
import { theme } from "@bounce/theme"
import { Button, PNImage, SliderNavigation } from "@bounce/web-components"

import { useSectionViewed } from "../../../hooks/useSectionViewed"
import SafeLink from "../SafeLink/SafeLink"

type PicturesSliderProps = {
  className?: string
  cta?: string
  href?: string
  name?: string
  pictures: { src: string; alt: string }[]
  subtitle?: string
  title?: string
}

const DEFAULT_SWIPER_BREAKPOINTS_CONFIG = {
  slidesOffsetBefore: 24,
  slidesOffsetAfter: 16,
  slidesPerView: 1.15,
  spaceBetween: 16,
}

const SWIPER_BREAKPOINTS_CONFIG = {
  [theme.spacing("screen-md")]: {
    ...DEFAULT_SWIPER_BREAKPOINTS_CONFIG,
    slidesPerView: 1.5,
    spaceBetween: 32,
  },
  [theme.spacing("screen-lg")]: {
    ...DEFAULT_SWIPER_BREAKPOINTS_CONFIG,
    slidesPerView: 2.15,
    spaceBetween: 32,
  },
  [theme.spacing("screen-xl")]: {
    ...DEFAULT_SWIPER_BREAKPOINTS_CONFIG,
    slidesPerView: 2.6,
    spaceBetween: 32,
  },
}

export const PicturesSlider = ({
  className,
  cta,
  href = "",
  name = "PicturesSlider",
  pictures,
  subtitle,
  title,
}: PicturesSliderProps) => {
  const swiperRef = useRef<SwiperRef | null>(null)
  const { sectionRef } = useSectionViewed({ name })

  return (
    <section
      ref={sectionRef}
      className={classNames(className, "relative mx-auto flex w-full max-w-screen-xl flex-col")}>
      {!!title && <Title2 className="mb-1 px-6">{title}</Title2>}
      {!!subtitle && <Subtitle className="mb-6 px-6">{subtitle}</Subtitle>}
      <Swiper
        breakpoints={SWIPER_BREAKPOINTS_CONFIG}
        className="w-full"
        ref={swiperRef}
        rewind
        watchSlidesProgress
        tag="ul"
        {...DEFAULT_SWIPER_BREAKPOINTS_CONFIG}>
        {pictures.map((picture, index) => (
          <SwiperSlide className="size-full h-96 overflow-hidden rounded-lg" key={index} tag="li">
            <PNImage src={picture.src} alt={picture.alt} width={640} height={640} className="size-full object-cover" />
          </SwiperSlide>
        ))}
      </Swiper>
      <div className="mt-8 flex w-full flex-wrap-reverse justify-between gap-8 px-6 md:gap-x-20 lg:mt-12">
        {!!cta && (
          <Button as={SafeLink} className="max-w-full flex-1 md:flex-none" href={href}>
            {cta}
          </Button>
        )}
        <SliderNavigation
          className="ml-auto"
          onClickNext={() => swiperRef.current?.swiper.slideNext()}
          onClickPrev={() => swiperRef.current?.swiper.slidePrev()}
          sliderItemSize={pictures.length}
        />
      </div>
    </section>
  )
}
